/**
 * Helpers for the clean, indexable dimension landing pages:
 *   /dack/:tireSize    e.g. /dack/205-55r16
 *   /falgar/:rimSize   e.g. /falgar/5-112-18-tum, /falgar/18-tum, /falgar/5-112
 *
 * Each helper turns a URL slug into the pub_* search params the SearchPage
 * already understands, plus a human label for the H1 / meta title.
 */

import { boltCircleSpellings, boltCircleText } from './boltCircleCore';

// ---- Tire dimension: "205-55r16" -> width / profile / diameter ----
export const parseTireSizeSlug = slug => {
  if (!slug) return null;
  const m = /^(\d{2,3})-(\d{2,3})r(\d{2})$/i.exec(String(slug).trim());
  return m ? { width: m[1], profile: m[2], diameter: m[3] } : null;
};

export const tireSizeToParams = slug => {
  const p = parseTireSizeSlug(slug);
  return p
    ? {
        pub_tireWidthFront: p.width,
        pub_profileFront: p.profile,
        pub_diameter: p.diameter,
      }
    : null;
};

export const tireSizeLabel = slug => {
  const p = parseTireSizeSlug(slug);
  return p ? `${p.width}/${p.profile} R${p.diameter}` : null;
};

// ---- Rim dimension ----
// Accepts:
//   "5-112-18-tum" -> bolts + circle + diameter
//   "18-tum"       -> diameter only
//   "5-112"        -> bolts + circle only
// A bolt-circle decimal is encoded with an underscore (e.g. 114_3), matching
// the existing pub_boltCircle URL convention.
export const parseRimSizeSlug = slug => {
  if (!slug) return null;
  const s = String(slug).trim().toLowerCase();
  const hasTum = /-tum$/.test(s);
  const core = hasTum ? s.replace(/-tum$/, '') : s;
  const tokens = core.split('-').filter(Boolean);

  if (hasTum && tokens.length === 1) return { diameter: tokens[0] };
  if (hasTum && tokens.length === 3) {
    return { bolts: tokens[0], circle: tokens[1], diameter: tokens[2] };
  }
  if (!hasTum && tokens.length === 2) return { bolts: tokens[0], circle: tokens[1] };
  return null;
};

// A bolt circle is stored in several spellings: Console's key ("114_3", and
// "1397" for 139.7 — what the wizard and, since P25009-488, every import
// write) and the plain number older imports wrote ("114.3"). Querying the
// slug spelling verbatim matched almost nothing, so every spelling is
// queried, which the API treats as "either".
const boltCircleSearchValue = circle => {
  if (!circle) return null;
  const spellings = boltCircleSpellings(circle);
  return spellings.length > 0 ? spellings.join(',') : circle;
};

export const rimSizeToParams = slug => {
  const p = parseRimSizeSlug(slug);
  if (!p) return null;
  const circleValues = boltCircleSearchValue(p.circle);
  return {
    pub_adType: 'rim',
    ...(p.bolts ? { pub_numberOfBolts: p.bolts } : {}),
    ...(circleValues ? { pub_boltCircle: circleValues } : {}),
    ...(p.diameter ? { pub_diameter: p.diameter } : {}),
  };
};

export const rimSizeLabel = slug => {
  const p = parseRimSizeSlug(slug);
  if (!p) return null;
  const circleDisplay = p.circle ? boltCircleText(p.circle) || p.circle.replace('_', '.') : null;
  const boltPart = p.bolts && circleDisplay ? `${p.bolts}×${circleDisplay}` : null;
  const diaPart = p.diameter ? `${p.diameter} tum` : null;
  return [boltPart, diaPart].filter(Boolean).join(', ');
};

// Build the slug used in footer links etc. from a "205/55R16" style label.
export const tireSizeSlugFromLabel = label => {
  const m = /^(\d{2,3})\/(\d{2,3})R(\d{2})$/i.exec(String(label).trim());
  return m ? `${m[1]}-${m[2]}r${m[3]}`.toLowerCase() : null;
};

// Build the slug from a "Fälgar 18 tum" or "Fälgar 5/114.3" style label:
//   "Fälgar N tum"  -> "{N}-tum"
//   "Fälgar X/Y"    -> "{X}-{Y}", bolt-circle decimal as an underscore (114.3 -> 114_3)
export const rimSizeSlugFromLabel = label => {
  const inchMatch = /(\d+)\s*tum/i.exec(String(label));
  if (inchMatch) return `${inchMatch[1]}-tum`;
  const boltMatch = /(\d+)\/(\d+(?:\.\d+)?)/.exec(String(label));
  if (boltMatch) return `${boltMatch[1]}-${boltMatch[2].replace('.', '_')}`;
  return null;
};

// The sizes we link to from the footer and the all-brands hub page. They live
// here so those two cannot drift apart — a size linked from one place and not
// the other would leave a page reachable from nowhere.
//
// Note the sitemap does not read these: it is server-side CommonJS and cannot
// import this module, and it has a better source anyway — it emits the sizes
// that actually have stock, so it never recommends an empty page.
const TIRE_SIZE_LABELS = [
  '195/65R15', '205/55R16', '205/60R16', '215/60R16', '225/45R17', '215/55R17', '225/50R17',
  '225/40R18', '235/45R18', '245/45R18', '235/45R19', '245/45R19', '255/45R19',
  '245/40R20', '255/40R20', '275/40R20', '255/35R21', '265/35R21', '275/35R21',
];

const RIM_SIZE_LABELS = [
  'Fälgar 15 tum', 'Fälgar 16 tum', 'Fälgar 17 tum', 'Fälgar 18 tum', 'Fälgar 19 tum',
  'Fälgar 20 tum', 'Fälgar 21 tum', 'Fälgar 22 tum',
  'Fälgar 4/100', 'Fälgar 4/108', 'Fälgar 5/100', 'Fälgar 5/108', 'Fälgar 5/112',
  'Fälgar 5/114.3', 'Fälgar 5/120', 'Fälgar 5/130',
];

export const POPULAR_TIRE_SIZES = TIRE_SIZE_LABELS.map(label => ({
  label,
  slug: tireSizeSlugFromLabel(label),
})).filter(size => size.slug);

export const POPULAR_RIM_SIZES = RIM_SIZE_LABELS.map(label => ({
  label,
  slug: rimSizeSlugFromLabel(label),
})).filter(size => size.slug);
